import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { apiRequest } from './api';

interface AuthState {
  user: {
    uid: string;
    email: string;
    user_type: string;
    client_id: string | null;
    client_type: string;
    role: string;
    signup_step: number;
  } | null;
  capabilities: string[];
  loading: boolean;
  hasCap: (cap: string) => boolean;
}

let cachedAuth: AuthState | null = null;

/** Invalidate cached auth — forces re-fetch on next render */
export function invalidateAuthCache() {
  cachedAuth = null;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(
    cachedAuth || {
      user: null,
      capabilities: [],
      loading: true,
      hasCap: () => false,
    },
  );

  const fetchMe = useCallback(async () => {
    try {
      const data = await apiRequest<{
        uid: string;
        email: string;
        user_type: string;
        client_id: string | null;
        client_type: string;
        role: string;
        capabilities: string[];
        signup_step: number;
      }>('/auth/me');

      const newState: AuthState = {
        user: {
          uid: data.uid,
          email: data.email,
          user_type: data.user_type,
          client_id: data.client_id,
          client_type: data.client_type,
          role: data.role,
          signup_step: data.signup_step ?? 4,
        },
        capabilities: data.capabilities,
        loading: false,
        hasCap: (cap: string) => data.capabilities.includes(cap),
      };

      cachedAuth = newState;
      setState(newState);
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        fetchMe();
      } else {
        const emptyState: AuthState = {
          user: null,
          capabilities: [],
          loading: false,
          hasCap: () => false,
        };
        cachedAuth = null;
        setState(emptyState);
      }
    });
    return () => unsubscribe();
  }, [fetchMe]);

  return state;
}
